/**
 * Product bundle upsell — companion checkbox rows on PDP.
 * Bridges selected companions to product-form multi-item cart add.
 */
if (!customElements.get('product-bundle-upsell')) {
  const formatMoney = (cents, format) => {
    if (typeof cents !== 'number') return '';
    const moneyFormat = format || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, value).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
  };

  class ProductBundleUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.rows = this.querySelectorAll('[data-bundle-offer-row]');
      this.ruleGroups = this.querySelectorAll('[data-bundle-rule-group]');
      this.bindEvents();
      this.bindVariantChange();

      const initialVariantId = parseInt(this.dataset.initialVariantId || '0', 10);
      if (initialVariantId) {
        this.updateRuleVisibility(initialVariantId);
      }
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
    }

    bindVariantChange() {
      if (typeof subscribe !== 'function') return;

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variantId = parseInt(event?.data?.variant?.id, 10);
        if (variantId) this.updateRuleVisibility(variantId);
      });
    }

    updateRuleVisibility(variantId) {
      let anyVisible = false;

      this.ruleGroups.forEach((group) => {
        const ids = (group.dataset.triggerVariantIds || '')
          .split(',')
          .filter(Boolean)
          .map((id) => parseInt(id, 10));
        const visible = ids.includes(variantId);

        group.toggleAttribute('hidden', !visible);

        if (!visible) {
          group.querySelectorAll('[data-bundle-checkbox]:checked').forEach((checkbox) => {
            checkbox.checked = false;
          });
        } else {
          anyVisible = true;
        }
      });

      this.toggleAttribute('hidden', !anyVisible);
      this.dispatchSelectionChange();
    }

    bindEvents() {
      this.rows.forEach((row) => {
        const checkbox = row.querySelector('[data-bundle-checkbox]');
        const variantSelect = row.querySelector('[data-bundle-variant-select]');

        row.addEventListener('click', (event) => {
          if (!checkbox || checkbox.disabled) return;

          // Title link navigates to the companion product — do not toggle.
          if (event.target.closest('a.bundle-offer-row__link--title')) return;

          // Native controls keep their own behavior.
          if (event.target.closest('[data-bundle-checkbox], select, label')) return;

          // Image link is part of the card hit area — select instead of navigating.
          if (event.target.closest('a.bundle-offer-row__link--media')) {
            event.preventDefault();
          }

          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        });

        checkbox?.addEventListener('change', () => {
          if (checkbox.checked && variantSelect?.tagName === 'SELECT') {
            const selected = variantSelect.options[variantSelect.selectedIndex];
            if (selected?.disabled) {
              checkbox.checked = false;
            }
          }
          this.dispatchSelectionChange();
        });

        variantSelect?.addEventListener('change', () => {
          this.updateRowPrices(row);
          if (checkbox && variantSelect.tagName === 'SELECT') {
            checkbox.value = variantSelect.value;
            const selected = variantSelect.options[variantSelect.selectedIndex];
            if (selected?.disabled) {
              checkbox.checked = false;
            }
          }
          this.dispatchSelectionChange();
        });
      });
    }

    dispatchSelectionChange() {
      this.enforceSelectionLimits();
      this.dispatchEvent(
        new CustomEvent('bundle-selection-change', {
          bubbles: true,
          detail: { selected: this.getSelectedCompanions() },
        })
      );
    }

    enforceSelectionLimits() {
      this.ruleGroups.forEach((group) => {
        const limit = Math.max(0, parseInt(group.dataset.editorialSelectionLimit || '0', 10));
        const checkboxes = [...group.querySelectorAll('[data-bundle-checkbox]')];
        checkboxes.filter((checkbox) => checkbox.checked).slice(limit).forEach((checkbox) => {
          checkbox.checked = false;
        });
        const full = checkboxes.filter((checkbox) => checkbox.checked).length >= limit;
        checkboxes.forEach((checkbox) => {
          if (checkbox.dataset.bundleLimitDisabled === 'true') {
            checkbox.disabled = false;
            delete checkbox.dataset.bundleLimitDisabled;
          }
          if (full && !checkbox.checked && !checkbox.disabled) {
            checkbox.disabled = true;
            checkbox.dataset.bundleLimitDisabled = 'true';
          }
        });
      });
    }

    generateBundleId() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      return `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    }

    getSelectedVariantElement(row) {
      const el = row.querySelector('[data-bundle-variant-select]');
      if (!el) return null;
      if (el.tagName === 'SELECT') {
        return el.options[el.selectedIndex];
      }
      return el;
    }

    getSelectedCompanions() {
      this.enforceSelectionLimits();
      const companions = [];
      this.rows.forEach((row) => {
        if (row.closest('[data-bundle-rule-group][hidden]')) return;

        const checkbox = row.querySelector('[data-bundle-checkbox]');
        if (!checkbox?.checked) return;

        const variantEl = this.getSelectedVariantElement(row);
        if (!variantEl || !variantEl.value) return;

        companions.push({
          variantId: parseInt(variantEl.value, 10),
          ruleId: row.dataset.ruleId,
          companionId: row.dataset.companionId,
          leadDay: parseInt(variantEl.dataset.leadDay || '0', 10),
          orderCutoffTime: parseInt(variantEl.dataset.orderCutoffTime || '-1', 10),
        });
      });
      return companions;
    }

    clearSelection() {
      this.querySelectorAll('[data-bundle-checkbox]:checked').forEach((checkbox) => {
        checkbox.checked = false;
      });
      this.dispatchSelectionChange();
    }

    updateRowPrices(row) {
      const variantEl = this.getSelectedVariantElement(row);
      if (!variantEl) return;

      const regularPrice = parseInt(variantEl.dataset.price || '0', 10);
      const compareAtPrice = parseInt(variantEl.dataset.compareAtPrice || '0', 10);
      const discountType = row.dataset.discountType;
      const discountValue = parseFloat(row.dataset.discountValue || '0');
      let bundlePrice = regularPrice;

      if (discountType === 'percent') {
        bundlePrice = regularPrice - Math.floor((regularPrice * discountValue) / 100);
      } else if (discountType === 'fixed_amount') {
        bundlePrice = regularPrice - Math.round(discountValue * 100);
      }

      if (bundlePrice < 0) bundlePrice = 0;

      let displayPrice = regularPrice;
      let displayCompare = regularPrice;
      let displaySavings = 0;
      let showSale = false;

      if (bundlePrice < regularPrice) {
        showSale = true;
        displayPrice = bundlePrice;
        displayCompare = regularPrice;
        displaySavings = regularPrice - bundlePrice;
      } else if (compareAtPrice > regularPrice) {
        showSale = true;
        displayPrice = regularPrice;
        displayCompare = compareAtPrice;
        displaySavings = compareAtPrice - regularPrice;
      }

      const priceEl = row.querySelector('[data-bundle-price]');
      const compareEl = row.querySelector('[data-bundle-compare-price]');
      const savingsEl = row.querySelector('[data-bundle-savings]');
      const compareWrap = row.querySelector('[data-bundle-compare-wrap]');
      const savingsWrap = row.querySelector('[data-bundle-savings-wrap]');
      const moneyFormat = this.dataset.moneyFormat;

      if (priceEl) {
        priceEl.textContent = formatMoney(displayPrice, moneyFormat);
      }

      if (compareEl) {
        compareEl.textContent = formatMoney(displayCompare, moneyFormat);
      }

      if (savingsEl) {
        savingsEl.textContent = formatMoney(displaySavings, moneyFormat);
      }

      if (compareWrap) {
        compareWrap.hidden = !showSale;
      }

      if (savingsWrap) {
        savingsWrap.hidden = !showSale;
      }
    }
  }

  customElements.define('product-bundle-upsell', ProductBundleUpsell);
}
